'use client';

/**
 * YarnBrowser — The core "My Yarns" browsing experience.
 *
 * ## Mental Model (as of build 1415)
 *
 * YarnBrowser is responsible for the **view layer** of the yarn library.
 * It owns the concept of "where the user currently is" while browsing yarns.
 *
 * It receives a flat list of yarns (already filtered by search or other top-level filters)
 * and a `sortMode`, and is responsible for:
 *
 * - Deciding whether to show a **grouped grid** or a **flat list**
 * - Managing **drill-down** state (when the user clicks into a color bucket, brand, or weight class)
 * - Rendering the appropriate navigation context (breadcrumbs)
 * - Coordinating between `<YarnGroupCard>` (grouped) and `<YarnCard>` (flat)
 *
 * ### Current Grouping Strategies (as of 2026-05)
 *
 * 1. **Section-based grouping** (`color`, `brand`, `weight`)
 *    - Renders a grid of `YarnGroupCard` components
 *    - Supports drill-in into a flat list of individual yarns
 *    - This is the primary model `YarnBrowser` owns in v1.
 *
 * 2. **Entity-based grouping** (`alphabet`)
 *    - Groups by (name + brand)
 *    - Uses a different drill mechanism (`groupFilter`)
 *    - Will be brought under `YarnBrowser` in a later slice.
 *
 * ### State Ownership
 *
 * YarnBrowser owns its own "drill-in" state:
 *   - colorDrillIn
 *   - brandDrillIn
 *   - weightDrillIn
 *
 * The parent (`yarn/page.tsx`) controls:
 *   - The base list of yarns (after search)
 *   - The current `sortMode`
 *   - The "Add new yarn" flow
 *   - The detail modal
 *
 * This separation keeps the page as a data + top-level control owner,
 * while YarnBrowser becomes the reusable, testable "browser" component.
 *
 * ### Future Evolution
 *
 * - Support for `alphabet` mode + `groupFilter`
 * - Possibly a `GroupingStrategy` abstraction if the two models diverge significantly
 * - URL sync for current drill-in state (nice-to-have)
 * - Virtualization for very large libraries
 *
 * @see src/app/yarn/page.tsx for current usage
 */

import { useState, useEffect } from 'react';
import { BackBreadcrumb } from '@/components/ui/BackBreadcrumb';
import { YarnGroupCard } from './YarnGroupCard';
import YarnCard from './YarnCard';
import { uncountedPartialGrams } from '@/lib/skeinFormat';
import type { Yarn } from '@/lib/types';
import type { ColorBucket } from '@/lib/colorBucket';

export type YarnSortMode = 'alphabet' | 'color' | 'brand' | 'weight';

type DrillIn =
  | { type: 'color'; value: ColorBucket | 'multicolor' }
  | { type: 'brand'; value: string } // '' means "No Brand"
  | { type: 'weight'; value: string }; // '' means "No Weight"

interface YarnBrowserProps {
  yarns: Yarn[];
  sortMode: Extract<YarnSortMode, 'color' | 'brand' | 'weight'>;
  onSelectYarn: (yarn: Yarn) => void;
  getYarnStats: (yarn: Yarn) => {
    available: number;
    inUse: number;
    rawAvailable: number;
  };
  sections: Array<{ key: string; label: string; yarns: Yarn[] }>;
  /** For correct color-based drill-in filtering. If not provided, color drill-in may show all yarns. */
  bucketFor?: (yarn: Yarn) => ColorBucket | 'multicolor';
}

export function YarnBrowser({
  yarns,
  sortMode,
  onSelectYarn,
  getYarnStats,
  sections,
  bucketFor,
}: YarnBrowserProps) {
  // === Internal Navigation State ===
  const [drillIn, setDrillIn] = useState<DrillIn | null>(null);

  // When the parent switches the top-level sort mode (alphabet ↔ color ↔ brand ↔ weight),
  // any drill-in that belonged to the previous mode is no longer valid. Clear it so we
  // always start the new mode in the grouped view.
  useEffect(() => {
    setDrillIn(null);
  }, [sortMode]);

  const isDrilledIn = drillIn !== null;

  // Compute the yarns to display when drilled in.
  // Parent passes `bucketFor` for accurate color-bucket matching (including
  // photo-derived buckets and explicit picks). Falls back to 'unknown' only
  // if not provided (defensive).
  const displayYarns = (() => {
    if (!drillIn) return yarns;

    if (drillIn.type === 'color') {
      const target = drillIn.value;
      const bf = bucketFor ?? (() => 'unknown' as const);
      return yarns
        .filter((y) => bf(y) === target)
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }

    if (drillIn.type === 'brand') {
      const targetBrand = drillIn.value;
      const isNoBrand = targetBrand === '';
      return yarns.filter((y) => {
        const b = (y.brand || '').trim();
        return isNoBrand ? b === '' : b === targetBrand;
      });
    }

    if (drillIn.type === 'weight') {
      const targetWeight = drillIn.value;
      const isNoWeight = targetWeight === '';
      return yarns.filter((y) => {
        const w = (y.weight || '').trim();
        const isUnset = !w || w === "I don't know";
        return isNoWeight ? isUnset : w === targetWeight;
      });
    }

    return yarns;
  })();

  // === Render: Drilled-in flat list ===
  if (isDrilledIn) {
    const breadcrumbLabel =
      drillIn?.type === 'color' ? 'All Colors' :
      drillIn?.type === 'brand' ? 'All Brands' : 'All Weights';

    return (
      <>
        <BackBreadcrumb label={breadcrumbLabel} onClick={() => setDrillIn(null)} />

        {displayYarns.map((yarn) => {
          const stats = getYarnStats(yarn);
          return (
            <YarnCard
              key={yarn.id}
              yarns={[yarn]}
              totalAvailable={stats.available}
              totalInUse={stats.inUse}
              totalPartialGrams={uncountedPartialGrams(yarn)}
              anyOver={stats.rawAvailable < 0}
              labels={{
                available: 'Available',
                inProgress: 'In Use',
                addColorway: 'Add colorway',
              }}
              onCardClick={() => onSelectYarn(yarn)}
            />
          );
        })}
      </>
    );
  }

  // === Grouped View (not drilled in) ===
  // Parent is responsible for computing sections via buildSections + bucketFor.
  // We just present them and own the drill-in click → state transition.
  const safeSections = sections ?? [];

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {safeSections.map((section) => (
        <YarnGroupCard
          key={section.key}
          section={section}
          getYarnStats={getYarnStats}
          onSelectYarn={onSelectYarn}
          onDrillIn={() => {
            if (sortMode === 'color') {
              setDrillIn({ type: 'color', value: section.key as any });
            } else if (sortMode === 'brand') {
              setDrillIn({ type: 'brand', value: section.key });
            } else {
              setDrillIn({ type: 'weight', value: section.key });
            }
          }}
          labels={{
            yarn: 'Yarn',
            yarnsPlural: 'Yarns',
            available: 'Available',
            inUse: 'In Use',
          }}
        />
      ))}
    </div>
  );
}
